import type { DynamicTexture } from "@babylonjs/core/Materials/Textures/dynamicTexture";
import { formatRaceOdds, type RacingState } from "@townsy/contracts";
import { ctx2d, mulberry32, type Ctx2D } from "../../scene/core/materials";
import { SADDLE_CLOTH, formatClock, liveOrder, ordinal } from "./model";

/**
 * Townsy Downs textures (ADR-0016), all painted here from code: the dirt, the mown grass, the betting hall floor,
 * the saddle-cloth numbers, small signs and the tote board. Original artwork, no external assets.
 */
export const RACE_COLORS = {
  green: "#1F6B47",
  greenDark: "#154C33",
  cream: "#F4ECDA",
  white: "#FAFAF7",
  rail: "#F7F7F2",
  concrete: "#CFC9BD",
  concreteDark: "#B5AE9F",
  steel: "#5A606B",
  board: "#14171D",
  amber: "#F2B632",
  dirt: "#9C6B43",
} as const;

export function paintDirt(tex: DynamicTexture): void {
  const ctx = ctx2d(tex);
  const size = tex.getSize().width;
  const rnd = mulberry32(911);
  ctx.fillStyle = RACE_COLORS.dirt;
  ctx.fillRect(0, 0, size, size);
  // Harrow lines along the running direction (u), then hoof-churned speckles.
  for (let i = 0; i < 26; i++) {
    const y = rnd() * size;
    ctx.fillStyle = rnd() < 0.5 ? "rgba(70,40,20,0.10)" : "rgba(255,230,200,0.07)";
    ctx.fillRect(0, y, size, 2 + rnd() * 3);
  }
  const specks = ["rgba(60,35,18,0.35)", "rgba(190,140,95,0.35)", "rgba(120,80,48,0.4)", "rgba(230,200,160,0.25)"];
  for (let i = 0; i < 5200; i++) {
    ctx.fillStyle = specks[Math.floor(rnd() * specks.length)]!;
    ctx.fillRect(rnd() * size, rnd() * size, 1 + rnd() * 3, 1 + rnd() * 2);
  }
  tex.update();
}

/** Grass with mowing stripes along v (two bands per tile). */
export function paintGrass(tex: DynamicTexture): void {
  const ctx = ctx2d(tex);
  const size = tex.getSize().width;
  const rnd = mulberry32(577);
  ctx.fillStyle = "#6FAE4E";
  ctx.fillRect(0, 0, size, size);
  ctx.fillStyle = "#7DBB59";
  ctx.fillRect(0, 0, size, size / 2);
  const specks = ["rgba(40,90,30,0.35)", "rgba(150,210,110,0.35)", "rgba(80,140,60,0.4)"];
  for (let i = 0; i < 4200; i++) {
    ctx.fillStyle = specks[Math.floor(rnd() * specks.length)]!;
    ctx.fillRect(rnd() * size, rnd() * size, 1 + rnd() * 2, 2 + rnd() * 3);
  }
  tex.update();
}

/** The betting hall floor: green with a cream border and "PLACE YOUR BETS" lettering facing the entrance. */
export function paintHallFloor(tex: DynamicTexture): void {
  const ctx = ctx2d(tex);
  const w = tex.getSize().width;
  const h = tex.getSize().height;
  ctx.fillStyle = "#2C7A52";
  ctx.fillRect(0, 0, w, h);
  ctx.strokeStyle = RACE_COLORS.cream;
  ctx.lineWidth = h * 0.035;
  ctx.strokeRect(h * 0.05, h * 0.05, w - h * 0.1, h - h * 0.1);
  ctx.fillStyle = "rgba(244,236,218,0.9)";
  ctx.font = `800 ${Math.round(h * 0.16)}px system-ui, "Segoe UI", sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("PLACE YOUR BETS HERE", w / 2, h * 0.5);
  tex.update();
}

/** Eight saddle-cloth numbers side by side (cell n-1 is number n). */
export function paintNumbers(tex: DynamicTexture): void {
  const ctx = ctx2d(tex);
  const w = tex.getSize().width;
  const h = tex.getSize().height;
  const cell = w / 8;
  for (let n = 1; n <= 8; n++) {
    const c = SADDLE_CLOTH[n]!;
    const x = (n - 1) * cell;
    ctx.fillStyle = c.cloth;
    ctx.fillRect(x, 0, cell, h);
    ctx.fillStyle = c.text;
    ctx.font = `900 ${Math.round(h * 0.78)}px system-ui, "Segoe UI", sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(String(n), x + cell / 2, h * 0.54);
  }
  tex.update();
}

/** A one-line sign: light text on a coloured plate. */
export function paintLabel(tex: DynamicTexture, text: string, bg: string, fg: string): void {
  const ctx = ctx2d(tex);
  const w = tex.getSize().width;
  const h = tex.getSize().height;
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, w, h);
  ctx.fillStyle = fg;
  // Fit the whole text (founder report 2026-10-13: "BETTING WINDOWS" was cut to "TING WIND").
  fit(ctx, text, w * 0.9, 800, Math.round(h * 0.6));
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text, w / 2, h * 0.54);
  tex.update();
}

const SANS = `system-ui, "Segoe UI", sans-serif`;

function fit(ctx: Ctx2D, text: string, maxWidth: number, weight: number, size: number): void {
  let s = size;
  ctx.font = `${weight} ${s}px ${SANS}`;
  while (s > 10 && ctx.measureText(text).width > maxWidth) {
    s -= 2;
    ctx.font = `${weight} ${s}px ${SANS}`;
  }
}

/**
 * The tote board (19 x 8 m in the infield, facing the stands): race number and phase with the countdown, the field in
 * two columns (number cloth, silks, name, odds; running order while they race; finishing order after), the last
 * result and the play-chips line.
 */
export function paintTote(tex: DynamicTexture, state: RacingState | null, seconds: number): void {
  const ctx = ctx2d(tex);
  const W = tex.getSize().width;
  const H = tex.getSize().height;
  ctx.fillStyle = RACE_COLORS.board;
  ctx.fillRect(0, 0, W, H);
  // Frame lines.
  ctx.fillStyle = RACE_COLORS.green;
  ctx.fillRect(0, 0, W, H * 0.16);
  ctx.fillRect(0, H * 0.9, W, H * 0.1);
  ctx.textBaseline = "middle";
  if (!state) {
    ctx.fillStyle = RACE_COLORS.cream;
    ctx.textAlign = "center";
    fit(ctx, "TOWNSY DOWNS", W * 0.9, 800, H * 0.2);
    ctx.fillText("TOWNSY DOWNS", W / 2, H / 2);
    tex.update();
    return;
  }
  const pad = W * 0.02;
  ctx.textAlign = "left";
  ctx.fillStyle = RACE_COLORS.cream;
  ctx.font = `800 ${Math.round(H * 0.095)}px ${SANS}`;
  ctx.fillText(`RACE ${state.raceId}`, pad, H * 0.08);
  const phaseText =
    state.phase === "betting"
      ? `BETTING  ${formatClock(seconds)}`
      : state.phase === "racing"
        ? state.finish.length > 0
          ? "PHOTO FINISH"
          : "THEY'RE OFF"
        : `RESULT  ·  NEXT ${formatClock(seconds)}`;
  ctx.textAlign = "right";
  ctx.fillStyle = state.phase === "racing" ? RACE_COLORS.amber : RACE_COLORS.cream;
  fit(ctx, phaseText, W * 0.6, 800, Math.round(H * 0.095));
  ctx.fillText(phaseText, W - pad, H * 0.08);

  const order = state.phase === "betting" ? [] : liveOrder(state);
  const rows = state.phase === "betting" ? state.horses.map((h) => h.n) : order;
  const colW = (W - pad * 3) / 2;
  const rowH = (H * 0.72) / 4;
  const top = H * 0.17;
  rows.forEach((n, i) => {
    const h = state.horses[n - 1];
    if (!h) return;
    const col = Math.floor(i / 4);
    const row = i % 4;
    const x = pad + col * (colW + pad);
    const y = top + row * rowH + rowH * 0.08;
    const bh = rowH * 0.84;
    const cloth = SADDLE_CLOTH[n]!;
    // Running / finishing position, then the number cloth.
    let cx = x;
    if (state.phase !== "betting") {
      ctx.fillStyle = i === 0 ? RACE_COLORS.amber : RACE_COLORS.cream;
      ctx.textAlign = "left";
      ctx.font = `800 ${Math.round(bh * 0.5)}px ${SANS}`;
      ctx.fillText(ordinal(i + 1), cx, y + bh / 2);
      cx += bh * 1.15;
    }
    ctx.fillStyle = cloth.cloth;
    ctx.fillRect(cx, y, bh, bh);
    ctx.fillStyle = cloth.text;
    ctx.textAlign = "center";
    ctx.font = `900 ${Math.round(bh * 0.72)}px ${SANS}`;
    ctx.fillText(String(n), cx + bh / 2, y + bh * 0.54);
    // Silks stripe.
    ctx.fillStyle = h.silk.body;
    ctx.fillRect(cx + bh * 1.1, y, bh * 0.22, bh);
    ctx.fillStyle = h.silk.trim;
    ctx.fillRect(cx + bh * 1.1, y + bh * 0.38, bh * 0.22, bh * 0.24);
    // Name and odds.
    const oddsText = formatRaceOdds(h.odds);
    ctx.textAlign = "right";
    ctx.fillStyle = RACE_COLORS.amber;
    ctx.font = `800 ${Math.round(bh * 0.6)}px ${SANS}`;
    ctx.fillText(oddsText, x + colW, y + bh / 2);
    const oddsW = ctx.measureText(oddsText).width;
    const nameX = cx + bh * 1.5;
    ctx.textAlign = "left";
    ctx.fillStyle = state.result?.winner.n === n ? RACE_COLORS.amber : RACE_COLORS.white;
    fit(ctx, h.name, x + colW - oddsW - bh * 0.4 - nameX, 700, Math.round(bh * 0.6));
    ctx.fillText(h.name, nameX, y + bh / 2);
  });

  // Footer: the last result (or this one) and the play-chips line.
  const shown = state.result ?? state.last;
  ctx.fillStyle = RACE_COLORS.cream;
  ctx.textAlign = "left";
  ctx.font = `700 ${Math.round(H * 0.06)}px ${SANS}`;
  const footer = shown ? `RACE ${shown.raceId} RESULT: ${shown.order.slice(0, 3).join(" - ")}` : "WIN BETS · PLAY CHIPS";
  ctx.fillText(footer, pad, H * 0.95);
  ctx.textAlign = "right";
  ctx.fillText("PLAY CHIPS ARE JUST FOR FUN", W - pad, H * 0.95);
  tex.update();
}
